import React, { useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import { IconRotateClockwise2 } from '@tabler/icons-react';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreditDispensations from 'hooks/creditDispensation/useCreditDispensations';
import { CellProps } from 'react-table';
import { CreditDispensationListView } from 'types';

interface IAthleteCreditsTableContainerProps {
  athleteId: string | undefined;
  validityDate: Date | null;
}

const AthleteCreditsTableContainer = ({
  athleteId,
  validityDate,
}: IAthleteCreditsTableContainerProps): JSX.Element => {
  const { dateFormat } = useLocalization();

  const { data, isSuccess, isError, isLoading, hasNextPage, fetchNextPage } = useCreditDispensations({
    athleteId: athleteId,
    validityDate: validityDate ? dayjs(validityDate).format('YYYY-MM-DD') : undefined,
  });

  const [tableData, setTableData] = useState<CreditDispensationListView[]>([]);

  useEffect(() => {
    if (data) {
      const newData = data.pages.map(page => page.data.results).flat();
      setTableData(newData);
    }
  }, [data, validityDate, setTableData]);

  const columns = [
    {
      Header: 'Event Type',
      accessor: 'name',
    },
    {
      Header: 'Validity Period',
      accessor: 'dispenseDate',
      Cell: ({ row }: CellProps<CreditDispensationListView>) => (
        <>
          <Text>
            {dayjs(row.original.dispenseDate).format(dateFormat)} -{' '}
            {row.original.isRollover == false && // Show package's cancellation date if the credit is not a rollover and package's cancellation date is before expiration date
            row.original.athletePackageCancellationDate && // Clearer version of the code can be seen in CreditDispensationRow.tsx
            row.original.expirationDate && // Couldn't find a better way to do this inside the cell
            dayjs(row.original.athletePackageCancellationDate) < dayjs(row.original.expirationDate)
              ? dayjs(row.original.athletePackageCancellationDate).format(dateFormat)
              : row.original.expirationDate
              ? dayjs(row.original.expirationDate).format(dateFormat)
              : 'None'}
          </Text>
          {dayjs(row.original.athletePackageCancellationDate) < dayjs(row.original.expirationDate) && (
            <Text>Early Cancellation</Text>
          )}
        </>
      ),
    },
    {
      Header: 'Usage',
      accessor: 'creditUsage',
      Cell: ({ row }: CellProps<CreditDispensationListView>) => (
        <Text>
          {row.original.creditsUsed} / {row.original.credits ? row.original.credits : '∞'}
        </Text>
      ),
    },
    {
      Header: 'Rollover',
      accessor: 'isRollover',
      Cell: ({ value }: CellProps<CreditDispensationListView>) =>
        value ? <IconRotateClockwise2 style={{ color: 'green', marginLeft: '12px' }} /> : <></>,
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isError={isError}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />
    </>
  );
};

export default AthleteCreditsTableContainer;
